import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismIllustration,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { lte } from 'ember-truth-helpers';
import ENV from 'lumen/config/environment';

interface HotelAndMarketModalsModalTrialIndicationSignUpTodaySignature {
  Args: {
    afterCloseAction: unknown;
  };
}

export default class HotelAndMarketModalsModalTrialIndicationSignUpToday extends Component<HotelAndMarketModalsModalTrialIndicationSignUpTodaySignature> {
  @service declare alerting: Services['alerting'];
  @service declare intl: Services['intl'];
  @service declare productState: Services['product-state'];

  @tracked hasContactedSales = false;
  @tracked isTrialIndicationSubmitting = false;
  staticPrefix = ENV.staticURL;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get daysLeft() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.productState?.currentProductState?.days_left;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasTrialExpired() {
    return this.daysLeft === 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get modalTitle() {
    if (this.hasContactedSales) {
      return this.intl.t('general.trial_indication.request_send.success_title');
    }

    if (this.daysLeft > 7) {
      return this.intl.t('general.trial_indication.modal.upgrade_title', {
        product: this.intl.t(`general.${this.productState?.currentProduct}`),
      });
    }

    if (this.daysLeft <= 7 && this.daysLeft > 0) {
      return this.intl.t('trial_indication.modal.nearly_expired');
    }

    if (this.hasTrialExpired) {
      return this.intl.t('trial_indication.modal.expired');
    }

    return `${this.intl.t('general.trial_indication.expired.modal_title')} - ${this.intl.t(
      'general.trial_indication.sign_up_today',
    )}`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get productSubscribeText() {
    return this.intl.t(
      `general.trial_indication.subscribe_text.${this.productState?.currentProduct}`,
    );
  }

  @action
  closeModal(): void {
    // @ts-expect-error: Incorrect type
    this.args.afterCloseAction();
  }

  @action
  getInTouch(): void {
    this.isTrialIndicationSubmitting = true;

    this.productState.sendTrialContactRequest(
      () => {
        // On success
        this.isTrialIndicationSubmitting = false;
        this.hasContactedSales = true;
      },
      () => {
        // On already requested
        this.isTrialIndicationSubmitting = false;
        this.hasContactedSales = true;
      },
      () => {
        // On error
        this.isTrialIndicationSubmitting = false;
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert();
      },
    );
  }

  <template>
    {{! @glint-expect-error: Incorrect type }}
    <PrismModal @onClose={{@afterCloseAction}} @title={{this.modalTitle}}>
      <div class="modal--small">
        {{#if this.hasContactedSales}}
          <div
            class="modal-trial-indication-image-wrapper modal-trial-indication-image-wrapper--success flex justify-center"
          >
            <PrismIllustration
              @name="320x320_message-sent"
              @size="large"
              aria-label={{this.productSubscribeText}}
            />
          </div>
          <div
            class="modal-trial-indication-sign-up-content modal-trial-indication-sign-up-content--success centered"
          >
            <h2 class="emphasis sign-up-success-title">
              {{t "general.trial_indication.request_sent.success_title"}}
            </h2>
            <p class="main-text paragraph-300--regular">
              {{t "general.trial_indication.request_sent.success_text"}}
            </p>
          </div>
        {{else}}
          <div class="modal-trial-indication-image-wrapper flex justify-center">
            {{#if this.hasTrialExpired}}
              <PrismIllustration
                @name="320x320_trial-end"
                @size="large"
                aria-label={{this.productSubscribeText}}
              />
              {{! @glint-expect-error: Incorrect type }}
            {{else if (lte this.productState.currentProductState.days_left 7)}}
              <PrismIllustration
                @name="320x320_trial-middle"
                @size="large"
                aria-label={{this.productSubscribeText}}
              />
            {{else}}
              <PrismIllustration
                @name="320x320_trial-start"
                @size="large"
                aria-label={{this.productSubscribeText}}
              />
            {{/if}}
          </div>
          <div class="modal-trial-indication-sign-up-content">
            <h2>{{this.modalTitle}}</h2>
            <br />
            <p class="main-text paragraph-300--regular">
              {{t "general.trial_indication.greeting"}}
              <br /><br />
              {{#if this.hasTrialExpired}}
                {{t "general.trial_indication.expired.friendly_reminder"}}
              {{else}}
                {{t "general.trial_indication.friendly_reminder"}}
              {{/if}}
              <br /><br />
              {{this.productSubscribeText}}
              <br /><br />
              {{t "general.trial_indication.best_regards"}}
              <br />
              {{t "general.trial_indication.lighthouse_team"}}
            </p>
            <div class="modal-trial-indication-cta-wrapper">
              <PrismButton
                {{! @glint-expect-error: Incorrect type }}
                @buttonType={{if this.canSignUp "secondary" "primary"}}
                @isDisabled={{this.isTrialIndicationSubmitting}}
                @label={{t "general.trial_indication.cta"}}
                @onClick={{this.getInTouch}}
              />
            </div>
            <p class="postscript-text text-200--regular">
              {{#if this.hasTrialExpired}}
                {{t "general.trial_indication.expired.ps_text"}}
              {{else}}
                {{t "general.trial_indication.ps_text"}}
              {{/if}}
            </p>
          </div>
        {{/if}}
      </div>
    </PrismModal>
  </template>
}
